import { useQuery } from "@tanstack/react-query";
import billingAPI, { Subscription } from "@/api/billing-api";
import { useWorkspaceStore } from "@/store/workspace-store";

export function useSubscription() {
  const { activeWorkspace } = useWorkspaceStore();

  const { data: subscription, isLoading, error } = useQuery<Subscription | null>({
    queryKey: ["subscription", activeWorkspace?._id],
    queryFn: () => {
      if (!activeWorkspace?._id) return null;
      return billingAPI.getCurrentSubscription(activeWorkspace._id);
    },
    enabled: !!activeWorkspace?._id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on 403 errors
  });

  const getDaysRemaining = (endTimestamp?: number): number => {
    if (!endTimestamp) return 0;
    const now = new Date().getTime();
    const end = new Date(endTimestamp * 1000).getTime();
    const diffInMs = end - now;
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    return Math.max(0, diffInDays);
  };

  const trialDaysRemaining =
    subscription?.is_trialing && subscription?.trial_end
      ? getDaysRemaining(subscription?.trial_end)
      : null;

  // Check if error is 403 (user is not admin)
  const is403Error =
    error &&
    typeof error === "object" &&
    "response" in error &&
    (error as any).response?.status === 403;

  return {
    subscription,
    isLoading,
    error,
    isTrialing: subscription?.is_trialing,
    trialDaysRemaining,
    trialEndDate: subscription?.trial_end,
    isAdmin: !is403Error, // If no 403 error, user is admin (or has access)
  };
}
